// The page's copy of the rows: a sparse array the size of the session, filled a stretch at a
// time from the Worker as the list scrolls, and patched with each operation's change. The Worker
// answers in the order it was asked, so a view asked before an operation always lands before
// that operation's change, and every answer agrees with the rows the page holds when it lands.

import type { Engine } from '../engine/client.ts'
import type { Change, RowView } from './types.ts'

const CHUNK = 200

export class Rows {
  private items: (RowView | undefined)[] = []
  private asked = new Set<number>()
  constructor(private engine: Engine, private changed: () => void) {}

  reset(len: number) {
    this.items = new Array(len)
    this.asked.clear()
  }

  get(row: number): RowView | undefined {
    return this.items[row]
  }

  get length(): number {
    return this.items.length
  }

  /// Asks for the rows in `from..to` the page does not have, a chunk at a time.
  ensure(from: number, to: number) {
    to = Math.min(to, this.items.length)
    for (let start = Math.floor(Math.max(0, from) / CHUNK) * CHUNK; start < to; start += CHUNK) {
      if (this.asked.has(start)) continue
      const end = Math.min(start + CHUNK, this.items.length)
      let missing = false
      for (let k = start; k < end && !missing; k++) missing = this.items[k] === undefined
      if (!missing) continue
      this.asked.add(start)
      void this.engine.request('view', { from: start, to: end }).then(rows => {
        this.asked.delete(start)
        rows.forEach((row, k) => (this.items[start + k] = row))
        this.changed()
      }, () => this.asked.delete(start))
    }
  }

  /// Applies an operation's change: its rows replace `removed` rows from `at`.
  splice(change: Change, rows: RowView[], len: number) {
    this.items.splice(change.at, change.removed, ...rows)
    if (this.items.length !== len) this.items.length = len
    this.asked.clear()
    this.changed()
  }
}
